import { fetchPinInvalidAttempts } from '@api/useQueryCalls';
import IconRightArrow from '@atoms/IconRightArrow';
import SecurityPinBanner from '@atoms/SecurityPinBanner';
import SkeletonComponent from '@atoms/SkeletonComponent';
import { Divider, Text, View } from '@components/primitives';
import useFeatureFlag from '@customHooks/useFeatureFlag';
import BottomSheet from '@molecules/LoginInfoSheet';
import ProfileActivePinRow from '@molecules/ProfileActivePinRow';
import profilePinActiveBottomSheetHandler from '@molecules/ProfileActivePinRow/profilePinRowUtils';
import PhoneChangeRow from '@molecules/ProfilePhoneChangeRow';
import ProfileRejectedPinRow from '@molecules/ProfileRejectedPinRow';
import navigationConstants from '@navigation/navigationConstants';
import { onCheckSecurityErrorCallBack, onCheckSecurityPinCallBack } from '@organisms/ProfileTab/profileUtils';
import { useNavigation } from '@react-navigation/native';
import { FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import { MODAL_KEYS, getModalKeyBasedOnFeatureFlags } from '@screens/GGCreatePin/pinUtils';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { SVG_ICONS } from '@utils/SvgIcons';
import { returnBottomSheetContent } from '@utils/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, Platform, StyleSheet } from 'react-native';
import logSecurityPin from './analyticsProfileSecurityPin';

const styles = StyleSheet.create({
  container: {
    marginLeft: 16,
  },
  phoneRowContainer: {
    marginVertical: 8,
  },
  rowContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginVertical: 8,
  },
});

interface PinRegister {
  isActive: boolean;
  showPinRegister?: boolean;
}

interface InfoModal {
  visible: boolean;
  data: {
    title: string;
    buttonText: string;
    hasTwoButtons: boolean;
    description: string;
    description2?: string;
    icon?: React.ReactNode;
    buttonText2?: string;
    phoneNumber: string;
  };
  errorCode: string;
}

interface ProfileSecurityPinProps {
  showSecurityPin?: boolean;
  route?: string;
  showPhoneChange?: boolean;
}

export function onPressSecurityPin(navigation: any) {
  return () => {
    logSecurityPin();
    navigation.navigate(navigationConstants.GG_CREATE_PIN, { type: 'create', source: 'pin_creation' });
  };
}

export function getSecurityPinStatusView(pinRegister: PinRegister, childRef: React.RefObject<any>) {
  if (pinRegister.isActive) {
    return <ProfileActivePinRow childRef={childRef} />;
  }
  return <ProfileRejectedPinRow childRef={childRef} onPressSecurityPin={onPressSecurityPin} />;
}

export function onParentPressHandler(childRef: React.RefObject<any>) {
  return () => {
    if (childRef?.current) {
      if (childRef.current.click) {
        childRef.current.click(); // Web click
      }
      if (childRef.current.props?.onPress) {
        childRef.current.props.onPress(); // Mobile press
      }
    }
  };
}

const ProfileSecurityPin: React.FC<ProfileSecurityPinProps> = ({
  showSecurityPin = false,
  route = '',
  showPhoneChange = false,
}) => {
  const { t } = useTranslation('common');
  const navigation = useNavigation<any>();
  const store = getStore();
  const pinRegister = store.pinRegister as PinRegister;
  const setShowPinRegister = store.setShowPinRegister as (val: boolean) => void;
  const childRef = useRef<any>(null);
  const childRefPhoneChange = useRef<any>(null);
  const PadLock = SVG_ICONS.securityPin.padLock;
  const PhoneChangeSvg = SVG_ICONS.phoneChange.phoneChange;

  // Feature flag for modal logic
  const [isFeatureAvailale] = useFeatureFlag({
    featureName: [FEATURE_FLAGS.BANK_SEL_TRNSF, FEATURE_FLAGS.PHONE_ENROLL],
  });

  // Modal state for direct handling
  const [infoModal, setInfoModal] = useState<InfoModal>({
    visible: false,
    data: returnBottomSheetContent(
      MODAL_KEYS.GG_01_FALLBACK,
      SECURITY_PIN_INFO_MODAL[MODAL_KEYS.GG_01_FALLBACK],
    ) as InfoModal['data'],
    errorCode: MODAL_KEYS.GG_01_FALLBACK,
  });

  const { isSuccess, data, error, refetch, isLoading } = fetchPinInvalidAttempts() as {
    isSuccess: boolean;
    isLoading: boolean;
    isError: boolean;
    data?: any; // If there's a strict type for `fetchPinInvalidAttempts`, replace `any` with it
    error?: any;
    refetch: () => void;
  };

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      onCheckSecurityPinCallBack(setShowPinRegister)(data);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (error) {
      onCheckSecurityErrorCallBack(setShowPinRegister)();
    }
  }, [error]);

  // Wrapper function to match the expected signature
  const setInfoModalWrapper = (modal: InfoModal) => {
    setInfoModal(modal);
  };

  // Get proper navigation handlers from profilePinRowUtils
  const { onPrimaryPress, onPressSecondaryHandler, onCloseModal } = profilePinActiveBottomSheetHandler(
    setInfoModalWrapper,
    infoModal,
    navigation,
  );

  if (isLoading) {
    return <SkeletonComponent />;
  }

  const handleSecurityPinPress: () => void = () => {
    logSecurityPin();

    // Check PIN status and handle accordingly
    if (pinRegister.isActive) {
      // PIN is active - directly trigger the bottom sheet action
      const getModalNameKey = getModalKeyBasedOnFeatureFlags(isFeatureAvailale)();

      // Set the modal state directly (same as ProfileActivePinRow does)
      setInfoModal({
        visible: true,
        data: returnBottomSheetContent(getModalNameKey, SECURITY_PIN_INFO_MODAL[getModalNameKey]) as InfoModal['data'],
        errorCode: getModalNameKey,
      });
    } else {
      // PIN is not set - navigate to PIN creation screen directly
      // This is the same action that ProfileRejectedPinRow would perform
      navigation.navigate(navigationConstants.GG_CREATE_PIN, { type: 'create', source: 'pin_creation' });
    }
  };

  const handlePhoneChangePress: () => void = () => {
    // For PWA, we'll use a different approach - we'll trigger the action directly
    if (Platform.OS === 'web') {
      // The child component has a TouchableWithoutFeedback with testID="btnPressPhoneChange"
      const childButton = document.querySelector('[data-testid="btnPressPhoneChange"]');
      if (childButton) {
        (childButton as any).click();
      }
    } else if (childRefPhoneChange?.current?.props?.onPress) {
      // For mobile, call the onPress handler directly
      childRefPhoneChange.current.props.onPress();
    }
  };

  if (showSecurityPin) {
    if (pinRegister.showPinRegister && route === 'Home') {
      return <SecurityPinBanner />;
    }
    if (route !== 'Home') {
      return (
        <>
          <View style={styles.container} marginY="16px">
            <Text testID="txtProfileInformation" variant="md-bold">
              {t('profile.securityHeader')}
            </Text>

            <TouchableOpacity onPress={handleSecurityPinPress} style={styles.rowContainer} activeOpacity={0.7}>
              <View paddingX={4} paddingY={4} backgroundColor={colors.secondary.softOrchid} borderRadius={12}>
                <PadLock />
              </View>
              <Text testID="txtPinStatus" marginLeft="8px" variant="md-normal" flex={1}>
                {t('profile.pinsText')}
              </Text>
              {getSecurityPinStatusView(pinRegister, childRef)}
              <IconRightArrow />
            </TouchableOpacity>
            {showPhoneChange ? (
              <TouchableOpacity onPress={handlePhoneChangePress} style={styles.phoneRowContainer} activeOpacity={0.7}>
                <View flexDirection="row" alignItems="center">
                  <View
                    backgroundColor={colors.secondary.softOrchid}
                    paddingX={4}
                    paddingBottom={4.5}
                    paddingTop={3.5}
                    justifyContent="center"
                    borderRadius={12}>
                    <PhoneChangeSvg />
                  </View>
                  <Text testID="txtPinStatus" marginLeft="8px" variant="md-normal" flex={1}>
                    {t('profile.phoneChange')}
                  </Text>
                  <View flexDirection="row" alignItems="center">
                    <PhoneChangeRow childRef={childRefPhoneChange} />
                    <IconRightArrow />
                  </View>
                </View>
              </TouchableOpacity>
            ) : null}
          </View>
          <Divider height="4px" style={{ backgroundColor: colors.neutral.mist }} />
          <BottomSheet
            onPressPrimary={onPrimaryPress}
            onPressSecondary={onPressSecondaryHandler}
            loginInfo={infoModal}
            onClose={onCloseModal}
          />
        </>
      );
    }
    return null;
  }
  return null;
};

export default ProfileSecurityPin;
